import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

const CATEGORIES = [
  'Data Subscription', 'Internet', 'Transport', 'Fuel', 'Generator',
  'Office Supplies', 'Printing', 'Utilities', 'Rent', 'Maintenance',
  'Staff Welfare', 'Marketing', 'Bank Charges', 'Miscellaneous'
];

export default function Expenses() {
  const { hasPermission, user } = useAuth();
  const [data, setData] = useState({ data:[], summary:[], total:0 });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [pendingExpenses, setPendingExpenses] = useState([]);
  const [showPending, setShowPending] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (from) qs.append('from', from);
    if (to) qs.append('to', to);
    get('/expenses?' + qs.toString()).then(setData).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [from, to]);
  useEffect(() => { get('/bank-accounts').then(d => setBankAccounts(d.data||[])); }, []);

  useEffect(() => {
    if (['super_admin','admin'].includes(user?.role)) {
      get('/expenses/pending').then(d => setPendingExpenses(d.data||[])).catch(()=>{});
    }
  }, []);

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this expense? It will be recorded in the cashbook.')) return;
    try { await put('/expenses/'+id+'/approve', {}); load(); get('/expenses/pending').then(d=>setPendingExpenses(d.data||[])); }
    catch (err) { alert(err.message); }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Reason for rejection:');
    if (reason === null) return;
    try { await put('/expenses/'+id+'/reject', { rejection_reason: reason }); load(); get('/expenses/pending').then(d=>setPendingExpenses(d.data||[])); }
    catch (err) { alert(err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await del('/expenses/' + id);
    load();
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">General Expenses</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Non-supplier expenses — data, transport, fuel, office costs etc.
          </p>
        </div>
        {hasPermission('bills:write') && (
          <button className="btn btn-primary" onClick={() => setModal({})}>+ Add Expense</button>
        )}
      </div>

      {/* Summary tiles */}
      {data.summary.length > 0 && (
        <div className="metrics" style={{marginBottom:20}}>
          <div className="tile">
            <div className="tile-lbl">Total Expenses</div>
            <div className="tile-val neg">{fmt(data.total)}</div>
          </div>
          {data.summary.slice(0,3).map(s => (
            <div className="tile" key={s.category}>
              <div className="tile-lbl">{s.category}</div>
              <div className="tile-val">{fmt(s.total)}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="search-bar">
        <label style={{fontSize:12,color:'var(--ink-light)'}}>From</label>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <label style={{fontSize:12,color:'var(--ink-light)'}}>To</label>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        {(from||to) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setFrom(''); setTo(''); }}>Clear</button>
        )}
      </div>

      <div className="tbl-wrap">
        {/* Pending approvals banner */}
      {['super_admin','admin'].includes(user?.role) && pendingExpenses.length > 0 && (
        <div style={{background:'#FFF3DC',border:'1px solid #F5C842',borderRadius:8,padding:'12px 20px',marginBottom:16}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:showPending?12:0}}>
            <div style={{fontSize:13,fontWeight:500,color:'#8B5E0A'}}>
              ⚠ <strong>{pendingExpenses.length}</strong> expense{pendingExpenses.length!==1?'s':''} above ₦10,000 awaiting your approval
            </div>
            <button className="btn btn-sm" style={{background:'#8B5E0A',color:'#fff',border:'none'}}
              onClick={()=>setShowPending(!showPending)}>
              {showPending?'Hide':'Review'}
            </button>
          </div>
          {showPending && (
            <div style={{background:'#fff',borderRadius:8,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'#8B5E0A'}}>
                  {['Date','Category','Description','Amount','Requested By',''].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#fff',textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {pendingExpenses.map((e,i) => (
                    <tr key={e.id} style={{background:i%2===0?'#fff':'#FFFBF0'}}>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{e.date}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{e.category}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>{e.description}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',fontFamily:'Cormorant Garamond,serif',fontSize:14,fontWeight:600,color:'#8B5E0A'}}>{fmt(e.amount)}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{e.recorded_by_name}</td>
                      <td style={{padding:'8px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <div style={{display:'flex',gap:6}}>
                          <button className="btn btn-sm" style={{background:'#2E6B3A',color:'#fff',border:'none',fontSize:11}}
                            onClick={()=>handleApprove(e.id)}>✓ Approve</button>
                          <button className="btn btn-danger btn-sm" style={{fontSize:11}}
                            onClick={()=>handleReject(e.id)}>✗ Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading ? <div className="spinner"></div> : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Description</th>
                <th>Method</th>
                <th style={{textAlign:'right'}}>Amount</th>
                <th>Recorded By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.data.length === 0
                ? <tr><td colSpan="7"><div className="empty-state"><h3>No expenses yet</h3></div></td></tr>
                : data.data.map(e => (
                  <tr key={e.id}>
                    <td>{e.date}</td>
                    <td><span className="badge b-grey">{e.category}</span></td>
                    <td className="text-muted text-sm">{e.description||'—'}</td>
                    <td><span className="badge b-blue">{e.payment_method}</span></td>
                    <td className="amt" style={{color:'var(--unpaid)'}}>{fmt(e.amount)}</td>
                    <td className="text-muted text-sm">{e.recorded_by_name||'—'}</td>
                    <td>
                      <div className="flex-center gap-sm">
                        {hasPermission('bills:write') && user?.role === 'super_admin' && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setModal(e)}>Edit</button>
                        )}
                        {hasPermission('bills:write') && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(e.id)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>

      {modal !== null && (
        <ExpenseModal
          expense={modal}
          bankAccounts={bankAccounts}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function ExpenseModal({ expense, onClose, onSaved, bankAccounts=[] }) {
  const {user}=useAuth();
  const isEdit = !!expense?.id;
  const [form, setForm] = useState(expense?.id ? expense : {
    date: new Date().toISOString().split('T')[0],
    category: '', description: '', amount: '', payment_method: 'cash', reference: '', bank_account_id: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [customCat, setCustomCat] = useState(!CATEGORIES.includes(expense?.category||''));
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateExplanation, setDuplicateExplanation] = useState('');

  const submitExpense = async (forceSave=false) => {
    if (!form.date || !form.category || !form.amount) {
      setError('Date, category and amount are required'); return;
    }
    if(form.payment_method!=='petty_cash'&&!form.bank_account_id){setError('Select the account used for this expense');return;}
    setSaving(true);
    try {
      if (isEdit) await put('/expenses/'+expense.id, form);
      else await post('/expenses', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:480}}>
        <div className="modal-hdr">
          <span className="modal-title">{isEdit ? 'Edit' : 'New'} Expense</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input type="date" className="form-control" value={form.date}
                onChange={e => setForm({...form, date:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Amount (N) *</label>
              <input type="number" className="form-control" value={form.amount}
                onChange={e => setForm({...form, amount:e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Category *</label>
            {customCat ? (
              <div className="flex-center gap-sm">
                <input className="form-control" placeholder="Type category"
                  value={form.category}
                  onChange={e => setForm({...form, category:e.target.value})} />
                <button className="btn btn-ghost btn-sm" onClick={() => setCustomCat(false)}>List</button>
              </div>
            ) : (
              <div className="flex-center gap-sm">
                <select className="form-control" value={form.category}
                  onChange={e => setForm({...form, category:e.target.value})}>
                  <option value="">Select category</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button className="btn btn-ghost btn-sm" onClick={() => setCustomCat(true)}>Custom</button>
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-control" placeholder="e.g. MTN 10GB data for office"
              value={form.description||''}
              onChange={e => setForm({...form, description:e.target.value})} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Payment Method</label>
              <select className="form-control" value={form.payment_method}
                onChange={e => setForm({...form, payment_method:e.target.value, bank_account_id:''})}>
                <option value="cash">Cash on Hand</option>
                <option value="petty_cash">Petty Cash</option>
                <option value="bank">Bank Transfer</option>
                <option value="pos">POS</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Reference</label>
              <input className="form-control" placeholder="Receipt no, ref..."
                value={form.reference||''}
                onChange={e => setForm({...form, reference:e.target.value})} />
            </div>
          </div>
          {form.payment_method!=='petty_cash' && (
            <div className="form-group">
              <label className="form-label">Paid From *</label>
              <select className="form-control" value={form.bank_account_id||''}
                onChange={e => setForm({...form, bank_account_id:e.target.value})}>
                <option value="">Select account...</option>
                {bankAccounts.filter(a=>a.type===(form.payment_method==='cash'?'cash':'bank')).map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.bank_name})</option>
                ))}
              </select>
            </div>
          )}
        </div>
        {showDuplicateWarning && duplicates.length > 0 && (
          <div style={{background:'#FDEAEA',border:'1px solid #F5B8B8',
            borderRadius:8,padding:16,margin:'0 20px 16px'}}>
            <div style={{fontSize:13,fontWeight:600,color:'#8F2E24',marginBottom:8}}>
              ⚠ Possible Duplicate Expense Detected
            </div>
            <div style={{fontSize:12,color:'#555',marginBottom:10}}>
              The following similar expenses already exist:
            </div>
            {duplicates.map(d => (
              <div key={d.id} style={{background:'#fff',borderRadius:6,padding:'8px 12px',
                marginBottom:6,fontSize:12,border:'1px solid #F5B8B8'}}>
                <strong>{d.date}</strong> — {d.category} — ₦{Number(d.amount).toLocaleString()}
                {d.reference && <span> — Ref: {d.reference}</span>}
                <div style={{color:'#8A8A8A',fontSize:11,marginTop:2}}>{d.description}</div>
              </div>
            ))}
            <div style={{marginTop:12}}>
              <label style={{fontSize:12,fontWeight:600,color:'#8F2E24',display:'block',marginBottom:4}}>
                Explanation (required to proceed) *
              </label>
              <textarea className="form-control" rows={2} value={duplicateExplanation}
                onChange={e=>setDuplicateExplanation(e.target.value)}
                placeholder="Explain why this is not a duplicate..." />
            </div>
            <div style={{display:'flex',gap:8,marginTop:10}}>
              <button className="btn btn-outline btn-sm"
                onClick={()=>{setShowDuplicateWarning(false);setDuplicates([]);}}>
                Cancel — Review First
              </button>
              <button className="btn btn-danger btn-sm"
                disabled={!duplicateExplanation}
                onClick={()=>submitExpense(true)}>
                Save Anyway — Not a Duplicate
              </button>
            </div>
          </div>
        )}
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={()=>submitExpense(false)} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : user?.role==='super_admin'?'Save & Approve Expense':'Add Expense'}
          </button>
        </div>
      </div>
    </div>
  );
}
